import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { PageSection } from '@components/page-section'
import { PlanComparisonCMS } from '@grammarly/website-components'
import { useCommonServices } from '@lib/hooks'
import { TypeComponentPlansModule } from '@lib/types/component-plans-module'
import { ContentProps } from '../renderer'
import { getIndividualPlans } from './utils'
import { PageContext } from '@contexts/page-context'
import { gnarClient, EventProps, gnarSpec } from '@lib/tracking/gnar'

export const Plans: React.FC<ContentProps<TypeComponentPlansModule>> = ({ structure }) => {
  const {
    sys,
    fields: { internalName, heading },
  } = structure

  const plans = getIndividualPlans(structure.fields)
  const { subscriptionService, localizedPricesIndiaService } = useCommonServices()
  const { proPlan } = subscriptionService
  const savingsLabel = structure.fields.savingsLabel.replace(/{{savings}}/, proPlan.percentageOfSavings.toString())

  const { pageId, slug } = useContext(PageContext)
  const gnarTrackFn = useCallback(
    (trackEventProps: EventProps) => gnarClient.track({ ...trackEventProps, pageId, pageSlug: slug }),
    [pageId, slug]
  )

  const eligibility = useMemo(() => {
    return localizedPricesIndiaService.isEligibleForLocalizedPlansIndiaAnon()
  }, [localizedPricesIndiaService])

  const displayPlan = useMemo(() => {
    if (eligibility.isEligible && eligibility.variantName) {
      return localizedPricesIndiaService.mapPlansToLocalizedPrice(proPlan, eligibility.variantName)
    }
    return proPlan
  }, [eligibility, localizedPricesIndiaService, proPlan])

  useEffect(() => {
    if (eligibility.isEligible && eligibility.variantName) {
      void gnarSpec.localizedPriceExperimentAnonTracked(pageId, slug, eligibility.variantName)
    }
  }, [pageId, slug, eligibility])

  return (
    <PageSection
      internalName={internalName}
      sysStructure={sys}
      heading={heading}
      overrideFirstEnterViewportThreshold={0.3}
    >
      <PlanComparisonCMS
        plans={plans}
        monthlyPrice={displayPlan.monthlyPrice}
        monthlyPricePerMonth={displayPlan.monthlyPricePerMonth}
        currency={displayPlan.currency}
        savingsLabel={savingsLabel}
        gnarTrackFn={gnarTrackFn}
        clientName="cms"
      />
    </PageSection>
  )
}
